import { baseApi } from '@/services/index'
import { IAddress, LOGIN_METHOD } from '@/types/services'

export interface IUuid {
  uuid: string
}

export interface IProfile extends IUuid, IEditableProfile {}

export interface IEditableProfile {
  first_name: string
  last_name: string
  phone: string
  estimated_fico: string
  consented_to_privacy_at: string
  consented_to_terms_at: string
  born_on: string
  social_security_number: string
  consented_to_credit_at: string
  marital_status: string
  mailing_address: IAddress
  email: string
  consent_to_credit?: boolean
  married_to_primary?: string
  spouse_profile_uuid?: string
  login_method?: LOGIN_METHOD
  middle_name?: string
  suffix?: string
}

interface Email {
  email: string
}

interface LoginResponse {
  token: string
  profile: IProfile
}

interface ListApplicationsForAddress {
  street: string
  city: string
  state: string
  zip_code?: string
}

interface DuplicatedAddressResponse {
  closed_lost_atypical: boolean
  has_application: boolean
  in_progress: boolean
}

interface IRequestedOffer {
  ctv: number
  exchange_rate: number
  offer_investment: number
  offer_investment_percentage: number
  terms: number
  tltv: number
  total_home_finance: number
  total_home_finance_cap: number
  unlock_share: number
  pricing_version?: number
}

interface ISignUpProfile {
  first_name: string
  last_name: string
  email: string
  phone: string
  estimated_fico?: string
  consented_to_privacy_at?: string
  consented_to_terms_at?: string
  is_manually_created?: boolean
  timezone: string
}

export interface ICreateApplicationPayload {
  is_new_user: boolean
  email: string
  fico?: string
  property: {
    address: IAddress
    estimated_value?: number
    estimated_debt: number
    property_type: string
    property_usage?: string
  }
  use_of_proceeds?: string[]
  max_offer?: IRequestedOffer
}

interface IRequestedOffers {
  max_value: number
  min_value: number
  offers: IRequestedOffer[]
  pricing_version: number
  success: boolean
  error: string
}

export interface ICustomerOffersPayload {
  estimated_value: number
  estimated_debt: number
  estimated_fico: string
  property_usage: string
  property_type: string
  address: IAddress
}

interface ApplicationCreated extends IUuid {
  external_id: string
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    signUp: builder.mutation<LoginResponse, ISignUpProfile>({
      query: (body) => ({
        url: 'v1/customer/signup/',
        method: 'POST',
        body,
      }),
    }),
    createApplication: builder.mutation<ApplicationCreated, ICreateApplicationPayload>({
      query: (payload) => ({
        url: 'v2/application/',
        method: 'POST',
        body: payload,
      }),
    }),
    checkDuplicatedAddress: builder.mutation<DuplicatedAddressResponse, ListApplicationsForAddress>(
      {
        query: ({ ...body }) => {
          return {
            url: `/v2/customer/applications/check-duplicated-address/`,
            method: 'POST',
            body,
          }
        },
      }
    ),
    getOffers: builder.query<IRequestedOffers, ICustomerOffersPayload>({
      query: ({ address, ...params }) => ({
        url: '/v2/customer/offers/',
        method: 'GET',
        params: { ...params, address: JSON.stringify(address) },
      }),
    }),
    triggerWelcomeEmail: builder.mutation<boolean, string>({
      query: (application_uuid) => ({
        url: 'v1/customer/trigger-welcome-email/',
        method: 'POST',
        body: { application_uuid },
      }),
    }),
    sendCustomerLoginMagicLink: builder.mutation<void, { applicationProfileUUID: string }>({
      query: ({ applicationProfileUUID }) => ({
        url: `assist-customer-login/${applicationProfileUUID}/`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useSignUpMutation,
  useCheckDuplicatedAddressMutation,
  useCreateApplicationMutation,
  useLazyGetOffersQuery,
  useTriggerWelcomeEmailMutation,
  useSendCustomerLoginMagicLinkMutation,
} = userApi
